/**
 * Persisted "last-used tenant" slug for consumer-facing surfaces.
 *
 * Consumer pages (login, public claim, onboarding) resolve their branding
 * from a `?tenant=<slug>` query param that we thread through every consumer
 * email link. When a customer lands on a page WITHOUT that param (e.g. they
 * bookmarked `/login`, or followed an older link that predates the param),
 * we fall back to the last tenant whose branding we successfully resolved
 * for them - rather than the backend's "first active tenant" default, which
 * is what caused Teknor Apex customers to see Supagard branding.
 *
 * Resolution order is always: tenant subdomain (`<slug>.domain`, authoritative
 * when present) > explicit slug (URL / route) > build-time `VITE_TENANT_SLUG` >
 * this stored fallback.
 */

import { getTenantSlugFromHost } from '@/shared/lib/tenant-host'

const STORAGE_KEY = 'raven:consumer:last-tenant-slug'
// Pre-rename key; migrate once so existing visitors keep their fallback.
const LEGACY_STORAGE_KEY = 'stream-x:consumer:last-tenant-slug'

/** Read the persisted fallback slug, migrating from the legacy key if needed. */
export function getStoredTenantSlug(): string {
  try {
    let value = window.localStorage.getItem(STORAGE_KEY)
    if (!value) {
      const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY)
      if (legacy) {
        window.localStorage.setItem(STORAGE_KEY, legacy)
        window.localStorage.removeItem(LEGACY_STORAGE_KEY)
        value = legacy
      }
    }
    return value || ''
  } catch {
    // localStorage can throw in private-mode / blocked-cookie contexts.
    return ''
  }
}

/** Persist the slug that explicitly resolved a tenant's branding. */
export function rememberTenantSlug(slug: string | null | undefined): void {
  if (!slug) return
  try {
    window.localStorage.setItem(STORAGE_KEY, slug)
  } catch {
    // Ignore - the fallback is best-effort.
  }
}

/**
 * Resolve the effective tenant slug for a branding lookup.
 *
 * The tenant subdomain wins whenever the customer is on `<slug>.domain` - it
 * is the authoritative selector and must override any stale `?tenant=` param or
 * stored fallback. On a generic host (`app.domain`, `localhost`) we fall back to
 * the explicit slug, the build-time tenant, then the last-resolved slug.
 *
 * @param explicitSlug slug from the URL query param or a route param
 */
export function resolveTenantSlug(explicitSlug?: string | null): string {
  return (
    getTenantSlugFromHost() ||
    (explicitSlug || '').trim() ||
    (import.meta.env.VITE_TENANT_SLUG ?? '') ||
    getStoredTenantSlug()
  )
}
